import { Text } from '@/components/text';
import { Colors } from '@/constants/theme';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import React, { useMemo, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { PortalDropdown } from './portal-dropdown';
import { Tooltip } from './tooltip';

export type SelectOption<T = string> = {
  value: T;
  label: string;
  tooltip?: string;
};

interface SelectProps<T = string> {
  label?: string;
  tooltip?: string;
  value: T;
  onValueChange: (value: T) => void;
  options: SelectOption<T>[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
}

export function Select<T extends string = string>({
  label,
  tooltip,
  value,
  onValueChange,
  options,
  placeholder = 'Select...',
  disabled,
  id,
  name,
}: SelectProps<T>) {
  const [open, setOpen] = useState(false);
  const selectedOption = useMemo(() => options.find(opt => opt.value === value), [options, value]);
  const containerRef = useRef<View>(null);
  const anchorRef = useRef<View>(null);

  const toggleOpen = () => {
    if (!disabled) {
      setOpen(!open);
    }
  };

  const handleSelect = (itemValue: T) => {
    onValueChange(itemValue);
    // Small delay to ensure the event finishes processing on all platforms
    setTimeout(() => setOpen(false), 50);
  };

  return (
    <View ref={containerRef} style={styles.container}>
      {label && (
        <View style={styles.labelRow}>
          <Text style={{ color: Colors.light.primary }}>{label}</Text>
          {tooltip && (
            <Tooltip content={tooltip}>
              <MaterialIcons name="info" size={18} color={Colors.light.primary} />
            </Tooltip>
          )}
        </View>
      )}
      <View ref={anchorRef} style={styles.anchorContainer} nativeID={id}>
        <Pressable
          {...({ id, name } as any)}
          onPress={toggleOpen}
          disabled={disabled}
          accessibilityRole="combobox"
          accessibilityLabel={label}
          style={[styles.trigger, open && styles.triggerActive, disabled && styles.disabled]}>
          <Text style={[styles.triggerText, !selectedOption && styles.placeholder]}>
            {selectedOption ? selectedOption.label : placeholder}
          </Text>
          <MaterialIcons name={open ? 'keyboard-arrow-up' : 'keyboard-arrow-down'} size={22} color={Colors.light.icon} />
        </Pressable>
      </View>

      <PortalDropdown
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        outsideClickRef={containerRef}
        maxHeight={250}
      >
        <ScrollView style={styles.list} keyboardShouldPersistTaps='always'>
          {options.map(item => (
            <TouchableOpacity
              key={item.value}
              onPress={() => handleSelect(item.value)}
              activeOpacity={0.7}
              style={[styles.option, item.value === value && styles.optionSelected]}>
              <View style={styles.optionContent}>
                <Text
                  type={item.value === value ? 'defaultSemiBold' : 'default'}
                  style={[styles.optionText, item.value === value && styles.optionTextSelected]}>
                  {item.label}
                </Text>
                {item?.tooltip && <Tooltip content={item.tooltip}>
                  <MaterialIcons name='info' size={18} color={Colors.light.primary} />
                </Tooltip>}
              </View>
              {item.value === value && <MaterialIcons name='check' size={18} color={Colors.light.primary} />}
            </TouchableOpacity>
          ))}
        </ScrollView>
      </PortalDropdown>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 10,
    width: '100%',
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  anchorContainer: {
    width: '100%',
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#898b8c',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: '#fff',
    minHeight: 48,
  },
  triggerActive: {
    borderColor: Colors.light.primary,
  },
  triggerText: {
    fontSize: 14,
  },
  placeholder: {
    color: 'rgba(0,0,0,0.4)',
  },
  disabled: {
    opacity: 0.5,
  },
  list: {
    width: '100%',
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  optionSelected: {
    backgroundColor: 'rgba(48, 87, 133, 0.05)',
  },
  optionText: {
    fontSize: 15,
  },
  optionTextSelected: {
    color: Colors.light.primary,
  },
  optionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  }
});
